// Requêtes de l'espace de supervision (lot L6-A, M24, TanStack Query), clés sous « supervision ». Le serveur
// applique périmètre, droit financier, partage, normalisations et masquages ; l'écran affiche ce qu'il reçoit.
import type {
  ComparaisonSupervision,
  CompetenceTransferee,
  CouvertureSupervision,
  CreationCompetence,
  ModificationCompetence,
  Normalisation,
  VersionCompetence,
} from '@cc/contrats';
import { useQuery } from '@tanstack/react-query';
import { requete } from '../../api/client';
import { telechargerFichier } from '../recettes/requetes';

export interface Filtres {
  province?: string;
  type?: 'commune' | 'conseil_departemental';
  mois_debut?: string;
  mois_fin?: string;
}

export interface Resultat<T = Record<string, unknown>> {
  perimetre: string;
  periode?: { mois_debut: string; mois_fin: string } | null;
  couverture: CouvertureSupervision;
  elements: T[];
}

export interface LigneRepertoire {
  collectivite_id: string;
  code_court: string;
  nom: string;
  type: string;
  province_code: string;
  province_nom: string;
  departement_nom: string | null;
  statut_deploiement: string;
  fictive: boolean;
  population_declaree: number | null;
}

export interface LigneDeploiement extends LigneRepertoire {
  modules_actifs: string[];
  nombre_modules_actifs: number;
  utilisateurs_actifs_30j: number;
  demarches_publiees: number | null;
  derniere_activite: string | null;
  calcule_le: string;
}

export const INVALIDER_SUPERVISION: readonly (readonly unknown[])[] = [['supervision']];

/** Paramètres de requête sans valeur vide. */
export function parametres(f: Record<string, string | undefined>): Record<string, string> {
  return Object.fromEntries(Object.entries(f).filter((e): e is [string, string] => Boolean(e[1])));
}

const lire = <T,>(chemin: string, p: Record<string, string | undefined> = {}) => ({ signal }: { signal: AbortSignal }) =>
  requete<T>(chemin, { signal, parametres: parametres(p) });

const options = { retry: false, staleTime: 60_000 } as const;

export const useRepertoire = (f: Filtres) =>
  useQuery({ queryKey: ['supervision', 'repertoire', f.province, f.type], queryFn: lire<Resultat<LigneRepertoire>>('/supervision/repertoire', { province: f.province, type: f.type }), ...options });

export const useDeploiement = (f: Filtres) =>
  useQuery({ queryKey: ['supervision', 'deploiement', f.province, f.type], queryFn: lire<Resultat<LigneDeploiement>>('/supervision/deploiement', { province: f.province, type: f.type }), ...options });

export const useActivite = (f: Filtres) =>
  useQuery({ queryKey: ['supervision', 'activite', f], queryFn: lire<Resultat>('/supervision/activite', { ...f }), ...options });

export const useComparaison = (q: Filtres & { indicateur: string; normalisation: Normalisation }, actif = true) =>
  useQuery({ queryKey: ['supervision', 'comparaison', q], queryFn: lire<ComparaisonSupervision>('/supervision/comparaison', { ...q }), enabled: actif, ...options });

export const useFinances = (f: Filtres, actif: boolean) =>
  useQuery({ queryKey: ['supervision', 'finances', f], queryFn: lire<Resultat>('/supervision/finances', { ...f }), enabled: actif, ...options });

export const useCompetences = (f: Filtres & { competence?: string }) =>
  useQuery({ queryKey: ['supervision', 'competences', f.province, f.type, f.competence], queryFn: lire<{ perimetre: string; colonnes: string[]; elements: CompetenceTransferee[] }>('/supervision/competences', { province: f.province, type: f.type, competence: f.competence }), ...options });

export const useHistoriqueCompetence = (id: string | null) =>
  useQuery({ queryKey: ['supervision', 'competences', 'historique', id], queryFn: lire<{ elements: VersionCompetence[] }>(`/supervision/competences/${id}/historique`), enabled: Boolean(id), ...options });

export const creerCompetence = (c: CreationCompetence) => requete<{ id: string; version: number }>('/supervision/competences', { methode: 'POST', corps: c });
export const modifierCompetence = (id: string, c: ModificationCompetence) => requete<{ id: string; version: number }>(`/supervision/competences/${id}`, { methode: 'PUT', corps: c });

/** Export d'une vue : mêmes filtres que l'écran ; le serveur choisit colonnes et lignes. */
export function exporter(vue: string, format: 'csv' | 'xlsx', p: Record<string, string | undefined>): Promise<void> {
  return telechargerFichier('/supervision/export', `supervision-${vue}.${format}`, parametres({ ...p, vue, format }));
}
